'use client';

import * as React from 'react';

import { cn } from '../../lib/utils';
import { BackHeaderButton } from '../patterns/back-link';
import { Logo } from '../brand/logo';
import { ScrollPane } from '../patterns/scroll-pane';
import {
  AuthHeroPanel,
  resolveAuthHeroVariant,
  type AuthHeroVariant,
} from './auth-hero-visual';

export interface AuthFullscreenLayoutProps {
  title: string;
  description?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  onBack?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  logoHref?: string;
  /** Progress bar width 0–100; shown when defined */
  progress?: number;
  /** Eyebrow above title, e.g. "Member signup · Step 2 of 6" */
  stepLabel?: string;
  /** Form column width — signup wizard uses wide */
  contentMaxWidth?: 'narrow' | 'wide';
  /** Dim form and show loading overlay during submit/navigation */
  busy?: boolean;
  busyMessage?: string;
  /** Changes trigger a short enter animation on the form panel and hero visual */
  stepKey?: string;
  /** Override hero visual; defaults to resolving from stepKey */
  heroVariant?: AuthHeroVariant;
}

export function AuthFullscreenLayout({
  title,
  description,
  heroTitle = 'Welcome back to your creative space',
  heroSubtitle,
  onBack,
  children,
  footer,
  className,
  logoHref = '/',
  progress,
  stepLabel,
  contentMaxWidth = 'narrow',
  busy = false,
  busyMessage = 'Please wait…',
  stepKey = 'default',
  heroVariant,
}: AuthFullscreenLayoutProps) {
  const contentWidthClass = contentMaxWidth === 'wide' ? 'max-w-2xl' : 'max-w-[420px]';
  const resolvedHeroVariant = heroVariant ?? resolveAuthHeroVariant(stepKey);

  return (
    <div className={cn('flex min-h-dvh w-full bg-background', className)}>
      <aside className="auth-flow-hero relative hidden min-h-dvh w-[42%] max-w-[560px] shrink-0 overflow-hidden lg:flex lg:flex-col">
        <AuthHeroPanel
          variant={resolvedHeroVariant}
          heroTitle={heroTitle}
          heroSubtitle={heroSubtitle}
        />
      </aside>

      <div className="relative flex min-h-dvh min-w-0 flex-1 flex-col">
        {onBack ? (
          <BackHeaderButton
            label="Back"
            onClick={onBack}
            className="absolute left-4 top-4 z-10 sm:left-6 sm:top-6"
          />
        ) : null}
        <ScrollPane className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
          <div className={cn('auth-flow-panel mx-auto w-full', contentWidthClass)}>
            <div className="mb-8 flex flex-col items-center text-center lg:items-start lg:text-left">
              <Logo href={logoHref} height={36} className="mb-6" />
              {progress != null ? (
                <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                  />
                </div>
              ) : null}
              {stepLabel ? (
                <p className="text-caption font-semibold uppercase tracking-wide text-primary">{stepLabel}</p>
              ) : null}
              <div className="transition-opacity duration-300">
                <h1
                  className={cn(
                    'text-headline font-semibold tracking-tight text-foreground',
                    stepLabel && 'mt-2',
                  )}
                >
                  {title}
                </h1>
                {description ? (
                  <p className="mt-2 text-body-sm leading-relaxed text-muted-foreground">{description}</p>
                ) : null}
              </div>
            </div>
            <div className="relative min-h-[280px]">
              <div data-auth-step={stepKey} className={cn(busy && 'pointer-events-none opacity-40')}>
                {children}
              </div>
              {busy ? (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[2px]"
                  aria-live="polite"
                  aria-busy="true"
                  role="status"
                >
                  <div
                    className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
                    aria-hidden
                  />
                </div>
              ) : null}
            </div>
            {footer && !busy ? <div className="mt-6">{footer}</div> : null}
          </div>
        </ScrollPane>
      </div>
    </div>
  );
}
